import { useEffect, useState, useRef, useCallback } from 'react'; // ## 1. Import useCallback
import Editor, { type OnMount } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { Button } from '@/components/ui/button';
import { assetService, type Asset } from '@/services/asset-service';
import { toast } from 'sonner';
import { Save, Loader2, RefreshCw, AlertTriangle } from 'lucide-react'; // ## 2. Import RefreshCw and AlertTriangle icons

type AssetRawDataEditorProps = {
  assetId: string;
};

export const AssetRawDataEditor = ({ assetId }: AssetRawDataEditorProps) => {
  const [jsonString, setJsonString] = useState<string>('');
  const [pristineJsonString, setPristineJsonString] = useState<string>(''); // ## 3. State for the original, unmodified JSON
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  // ## 4. Memoize the fetch function so it can be called directly
  const fetchAssetJson = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // For raw data editor, we specifically want the raw asset data, not the content from URI
      // So we'll continue using getAsset directly for raw data
      const asset = await assetService.getAsset(assetId);
      const formattedJson = JSON.stringify(asset.data, null, 2);
      setJsonString(formattedJson);
      setPristineJsonString(formattedJson); // Set both the active and pristine state
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    if (assetId) {
      fetchAssetJson();
    }
  }, [assetId, fetchAssetJson]);

  const handleSave = async () => {
    setIsSaving(true);

    try {
      JSON.parse(jsonString);
    } catch (parseError) {
      toast.error("Invalid JSON format. Please correct the errors before saving.");
      setIsSaving(false);
      return;
    }
    
    try {
      // Parse the JSON string to get the actual object for updating
      const jsonData = JSON.parse(jsonString);
      
      // Update the asset with the new JSON data
      await assetService.updateAsset(assetId, { data: jsonData });
      
      // ## 5. On successful save, update the pristine state to the new content
      setPristineJsonString(jsonString);
      toast.success("Asset saved successfully!");
    } catch (err) {
      console.error('Error saving asset:', err);
      toast.error("Failed to save asset. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditorDidMount: OnMount = (editorInstance) => {
    editorRef.current = editorInstance;
    setTimeout(() => {
      editorInstance.getAction('editor.action.formatDocument')?.run();
    }, 200);
  };
  
  // ## 6. Determine if the content has been modified
  const isModified = jsonString !== pristineJsonString;

  if (isLoading) return <div className="flex h-full w-full items-center justify-center">Loading Editor...</div>;
  if (error) return <div className="flex h-full w-full items-center justify-center text-destructive">{error}</div>;

  return (
    <div className="flex flex-col h-full w-full gap-2">
      {/* Warning banner for raw data editor */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-yellow-800">Raw Asset Data Editor</p>
            <p className="text-yellow-700 mt-1">
              This editor modifies the raw asset data structure. For editing JSON file content, 
              use the "JSON Content" tab instead. Changes made here affect the asset's metadata and data structure directly.
            </p>
          </div>
        </div>
      </div>
      
      <div className="flex justify-end gap-2">
        {/* ## 7. Add the Refresh button */}
        <Button variant="outline" onClick={fetchAssetJson} disabled={isLoading}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
        </Button>
        <Button onClick={handleSave} disabled={isSaving || !isModified}>
          {isSaving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
      <div className="flex-1 rounded-md overflow-hidden border">
        <Editor
          height="100%"
          language="json"
          theme="vs-dark"
          value={jsonString}
          onMount={handleEditorDidMount}
          onChange={(value) => setJsonString(value || '')}
          options={{
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            automaticLayout: true,
          }}
        />
      </div>
    </div>
  );
};